import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Copy, Trash2, Plus, Calendar, Info, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAlbumShares,
  useCreateAlbumShare,
  useDeleteAlbumShare,
  useUpdateAlbumShare,
  copyAlbumShareUrl,
} from '@/hooks/useAlbumShares';
import { useUserPlan, isFreePlan } from '@/hooks/usePlan';
import { cn } from '@/lib/utils';
import type { AlbumShareWithUrl } from '@omokage/shared';

interface AlbumShareDialogProps {
  albumId: string;
  albumName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ShareItemProps {
  share: AlbumShareWithUrl;
  isPro: boolean;
  onDelete: (shareId: string) => void;
  onUpdateSlug: (shareId: string, customSlug: string) => void;
}

/**
 * Validate expiry days input
 * Returns valid number or null if invalid
 */
function validateExpiryDays(value: string): number | null {
  const num = Number(value);
  // Check for integer, within range, and not NaN/Infinity
  if (!Number.isInteger(num) || num < 1 || num > 365 || !Number.isFinite(num)) {
    return null;
  }
  return num;
}

/**
 * Validate custom slug format
 * Must be alphanumeric with hyphens only, 3-50 characters
 */
function validateCustomSlug(slug: string): boolean {
  if (!slug) return true; // Empty is valid (will be unset)
  return /^[a-zA-Z0-9-]{3,50}$/.test(slug);
}

function ShareItem({ share, isPro, onDelete, onUpdateSlug }: ShareItemProps) {
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [customSlug, setCustomSlug] = useState(share.customSlug || '');
  const [copied, setCopied] = useState(false);

  const isExpired = useMemo(
    // eslint-disable-next-line react-hooks/purity
    () => share.expiresAt !== null && share.expiresAt * 1000 < Date.now(),
    [share.expiresAt]
  );
  const createdDate = new Date(share.createdAt * 1000).toLocaleDateString();
  const expiryDate = share.expiresAt
    ? new Date(share.expiresAt * 1000).toLocaleDateString()
    : '無期限';

  const handleCopy = async () => {
    const success = await copyAlbumShareUrl(share.shareUrl);
    if (success) {
      setCopied(true);
      toast.success('共有リンクをコピーしました');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('リンクのコピーに失敗しました');
    }
  };

  const handleSaveSlug = () => {
    // Validate custom slug format
    if (!validateCustomSlug(customSlug)) {
      toast.error('カスタムURLは英数字とハイフンのみ、3〜50文字で入力してください');
      return;
    }

    if (customSlug && customSlug !== share.customSlug) {
      onUpdateSlug(share.id, customSlug);
    }
    setIsEditingSlug(false);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-4 rounded-lg border',
        'bg-white/50 dark:bg-card/50 border-border',
        'hover:bg-white/80 dark:hover:bg-card/80 transition-colors'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <code className="text-sm font-mono bg-muted px-2 py-1 rounded" title={share.shareUrl}>
            {share.customSlug || share.token}
          </code>
          {isExpired && (
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-full bg-destructive/10 text-destructive"
              role="status"
            >
              期限切れ
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            aria-label="共有リンクをコピー"
            className={cn(
              'transition-colors',
              copied ? 'text-success hover:bg-success/10' : 'text-accent hover:bg-accent/10'
            )}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(share.id)}
            aria-label="共有リンクを削除"
            className="text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span>作成日: {createdDate}</span>
        <span className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          有効期限: {expiryDate}
        </span>
      </div>

      {isPro && (
        <div className="pt-2 border-t border-border">
          {isEditingSlug ? (
            <div className="flex gap-2">
              <Input
                value={customSlug}
                onChange={(e) => setCustomSlug(e.target.value)}
                placeholder="custom-url-slug"
                className="h-8 text-sm"
                maxLength={50}
              />
              <Button size="sm" onClick={handleSaveSlug}>
                保存
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditingSlug(false)}>
                キャンセル
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                カスタムURL: {share.customSlug || '未設定'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingSlug(true)}
                className="h-6 text-xs"
              >
                編集
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ShareSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

export function AlbumShareDialog({
  albumId,
  albumName,
  open,
  onOpenChange,
}: AlbumShareDialogProps) {
  const [expiresInDays, setExpiresInDays] = useState<string>('7');
  const [noExpiration, setNoExpiration] = useState<boolean>(true);

  const { data: shares, isLoading } = useAlbumShares(albumId);
  const { data: userPlan } = useUserPlan();
  const createShare = useCreateAlbumShare();
  const deleteShare = useDeleteAlbumShare();
  const updateShare = useUpdateAlbumShare();

  const isPro = userPlan?.planType === 'pro' || userPlan?.planType === 'ultimate';
  const canSetNoExpiration = !isFreePlan(userPlan?.planType ?? 'free');

  const handleCreateShare = async () => {
    try {
      let expiryDays: number | null = null;

      if (!noExpiration || !canSetNoExpiration) {
        const validatedDays = validateExpiryDays(expiresInDays);
        if (validatedDays === null) {
          toast.error('有効期限は1〜365の整数で入力してください');
          return;
        }
        expiryDays = validatedDays;
      }

      await createShare.mutateAsync({
        albumId,
        expiresInDays: expiryDays,
      });

      toast.success('共有リンクを作成しました');
      setExpiresInDays('7');
      setNoExpiration(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '共有リンクの作成に失敗しました';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (shareId: string) => {
    try {
      await deleteShare.mutateAsync({ shareId, albumId });
      toast.success('共有リンクを削除しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '共有リンクの削除に失敗しました';
      toast.error(errorMessage);
    }
  };

  const handleUpdateSlug = async (shareId: string, customSlug: string) => {
    // Validate before sending to server
    if (!validateCustomSlug(customSlug)) {
      toast.error('カスタムURLは英数字とハイフンのみ、3〜50文字で入力してください');
      return;
    }

    try {
      await updateShare.mutateAsync({
        shareId,
        albumId,
        data: { customSlug },
      });
      toast.success('カスタムURLを更新しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'カスタムURLの更新に失敗しました';
      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="custom-scrollbar sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>アルバムを共有: {albumName}</DialogTitle>
          <DialogDescription>
            このアルバムの公開共有リンクを作成・管理します。リンクを知っている人は誰でもアルバムを閲覧できます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Create new share */}
          <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/50">
            <h3 className="text-sm font-medium">新しい共有リンクを作成</h3>
            <div className="flex gap-2">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="noExpiration"
                    checked={noExpiration}
                    onChange={(e) => setNoExpiration(e.target.checked)}
                    disabled={!canSetNoExpiration}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                  />
                  <label
                    htmlFor="noExpiration"
                    className={cn(
                      'text-xs',
                      canSetNoExpiration ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    無期限にする
                  </label>
                  {!canSetNoExpiration && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Info className="h-3 w-3" />
                      Lite以上のプランで利用可能
                    </span>
                  )}
                </div>
                <label htmlFor="expiry" className="text-xs text-muted-foreground mb-1 block">
                  有効期限(日数)
                </label>
                <Input
                  id="expiry"
                  type="number"
                  min="1"
                  max="365"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  placeholder="7"
                  className="h-9"
                  disabled={noExpiration}
                />
              </div>
              <Button
                onClick={handleCreateShare}
                disabled={createShare.isPending}
                className="mt-auto"
              >
                <Plus className="mr-2 h-4 w-4" />
                {createShare.isPending ? '作成中...' : 'リンクを作成'}
              </Button>
            </div>
            {!isPro && (
              <p className="text-xs text-muted-foreground">
                Pro以上のプランでカスタムURLが利用可能です
              </p>
            )}
          </div>

          {/* Existing shares */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">有効な共有リンク</h3>
            {isLoading ? (
              <ShareSkeleton />
            ) : !shares || shares.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>共有リンクはありません</p>
                <p className="text-sm mt-2">上のフォームから共有リンクを作成してください</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shares.map((share) => (
                  <ShareItem
                    key={share.id}
                    share={share}
                    isPro={isPro}
                    onDelete={handleDelete}
                    onUpdateSlug={handleUpdateSlug}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
